import { useMemo } from "react";
import PageBanner from "@/components/feature/PageBanner";
import { SkeletonCard } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import { useApi } from "@/hooks/useApi";
import { useNow } from "@/hooks/useNow";
import type { LadderBrief } from "@/lib/types";
import LadderCard from "./components/LadderCard";
import { STATE_ORDER } from "./utils";

export default function LadderPage() {
  const { data, loading, error, reload, serverTs } =
    useApi<LadderBrief[]>("ladders.php");
  const now = useNow();

  const elapsed = serverTs ? Math.max(0, now / 1000 - serverTs) : 0;

  // 진행중 → 예정 → 종료 순 정렬
  const items = useMemo(() => {
    const list = data ?? [];
    return [...list].sort(
      (a, b) =>
        (STATE_ORDER[a.state] ?? 9) - (STATE_ORDER[b.state] ?? 9) || b.no - a.no
    );
  }, [data]);

  return (
    <>
      <PageBanner
        title="사다리게임"
        description="출발칸을 하나 골라 경품 추첨에 참여하세요."
        breadcrumb={[{ label: "사다리게임" }]}
      />

      <section className="mx-auto max-w-6xl px-4 md:px-6 py-10 md:py-12">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="사다리게임을 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon="ri-gift-2-line"
            title="진행 중인 사다리게임이 없습니다"
            description="다음 행사 때 만나요."
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((ladder) => (
              <LadderCard
                key={ladder.no}
                ladder={ladder}
                remaining={
                  ladder.state === "open"
                    ? Math.max(0, ladder.end_in - elapsed)
                    : undefined
                }
              />
            ))}
          </div>
        )}
      </section>
    </>
  );
}